import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const LOGIN_BG = "https://images.unsplash.com/photo-1606857521015-7f9fcf423740?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB0ZWxlY29tJTIwb2ZmaWNlJTIwd29ya2Vyc3xlbnwwfHx8fDE3ODQ5Mjg2NTZ8MA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    // Surfaced by the axios 401 interceptor when a token expires mid-session.
    if (typeof window === "undefined") return;
    const p = new URLSearchParams(window.location.search);
    if (p.get("idle") === "1") {
      toast.info("Session déconnectée après 6 minutes d'inactivité. Merci de vous reconnecter.");
    } else if (p.get("expired") === "1") {
      toast.info("Votre session a expiré. Merci de vous reconnecter.");
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenue ${user.full_name}`);
      nav(user.role === "rh" ? "/rh" : "/employe", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={LOGIN_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1A3B]/90 via-[#0033A0]/80 to-[#0066FF]/70" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <span className="font-heading font-black text-2xl tracking-tight">FIO<span className="text-white/70">TELECOM</span></span>
          </div>
        </div>
        <div className="relative z-10 max-w-lg">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-tight">
            Votre espace RH,<br />simple et efficace.
          </h1>
          <p className="mt-5 text-white/80 text-base leading-relaxed">
            Gérez vos congés, téléchargez vos bulletins de paie et demandez vos attestations de travail en quelques clics.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { t: "Congés", d: "Demande & validation" },
              { t: "Bulletins", d: "Accès sécurisé" },
              { t: "Attestations", d: "PDF officiel" },
            ].map((f) => (
              <div key={f.t} className="rounded-lg bg-white/10 backdrop-blur border border-white/15 p-4">
                <div className="font-heading font-semibold text-white">{f.t}</div>
                <div className="text-xs text-white/70 mt-1">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-white/60">© {new Date().getFullYear()} FIO TELECOM — Espace RH</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-5 sm:p-8 lg:p-12 min-h-[100dvh] lg:min-h-0">
        <div className="w-full max-w-md fade-in">
          <div className="lg:hidden mb-6 flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-[#0066FF] flex items-center justify-center text-white">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <span className="font-heading font-black text-2xl tracking-tight text-slate-900">FIO<span className="text-[#0066FF]">TELECOM</span></span>
          </div>

          <div className="mb-6 sm:mb-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Connexion</h2>
            <p className="text-sm text-slate-500 mt-2">Accédez à votre espace personnel</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Adresse e-mail</Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="prenom.nom@fiotelecom.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Mot de passe</Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs font-medium text-[#0066FF] hover:text-[#0052CC]"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11"
                />
              </div>
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0066FF] hover:bg-[#0052CC] transition-colors font-medium"
            >
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connexion…</> : "Se connecter"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
